import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useEffect } from 'react';

export default function BookProposal() {
  useAnalytics('Constitutional AI Book Proposal');

  useEffect(() => {
    // Set document title
    document.title = 'The Constitutional AI Implementation Playbook | Book Proposal';
    
    // Set meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Academic book proposal: The Constitutional AI Implementation Playbook - Engineering Auditable Trust for High-Stakes Systems. 17 chapters covering trust engineering, governance frameworks, and legal compliance for AI systems.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Constitutional AI book, AI legal framework, academic book proposal, AI safety book, product liability AI, AI accountability, NIST AI RMF, AI governance, machine learning law, AI regulation book');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Constitutional AI book, AI legal framework, academic book proposal, AI safety book, product liability AI, AI accountability, NIST AI RMF, AI governance, machine learning law, AI regulation book';
      document.head.appendChild(meta);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://algorithmicconsistency.org/book-proposal');

    // Open Graph Tags for Social Sharing
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'The Constitutional AI Implementation Playbook: Engineering Auditable Trust for High-Stakes Systems');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Comprehensive academic book proposal for The Constitutional AI Implementation Playbook. Bridging technical AI alignment, product liability law, and policy implementation. 17-chapter outline for MIT Press, Oxford, Cambridge publishers.');
    }

    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'article');
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://algorithmicconsistency.org/book-proposal');
    }

    const ogSiteName = document.querySelector('meta[property="og:site_name"]');
    if (ogSiteName) {
      ogSiteName.setAttribute('content', 'Constitutional AI Policy Institute');
    }

    const articleAuthor = document.querySelector('meta[property="article:author"]');
    if (articleAuthor) {
      articleAuthor.setAttribute('content', 'Alberto Rocha');
    }

    const articlePublishedTime = document.querySelector('meta[property="article:published_time"]');
    if (articlePublishedTime) {
      articlePublishedTime.setAttribute('content', '2025-12-15');
    }

  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        
        <article className="container mx-auto px-6 lg:px-12 py-20 max-w-5xl">
          {/* Header */}
          <header className="mb-16 text-center">
            <div className="inline-block bg-red-800 text-white px-4 py-2 text-sm font-bold mb-6 whitespace-nowrap">
              ACADEMIC BOOK PROPOSAL
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              The Constitutional AI Implementation Playbook:<br />
              Engineering Auditable Trust for High-Stakes Systems
            </h1>
            <p className="text-xl text-slate-600 mb-4">
              Academic Book Proposal &amp; Detailed Outline
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-slate-600 mt-6">
              <span>By <strong>Alberto Rocha</strong></span>
              <span>•</span>
              <span>December 2025</span>
            </div>
          </header>

          {/* Book Cover and Overview */}
          <section className="mb-16">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Book Cover */}
              <div className="relative">
                <div className="bg-slate-100 rounded-lg overflow-hidden shadow-2xl">
                  <img 
                    src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/d8a653f0c5e1b828aa552af92565980d.png"
                    alt="The Constitutional AI Implementation Playbook Book Cover"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="mt-6 text-center">
                  <a 
                    href="/playbook-executive-summary" 
                    className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-6 py-3 font-semibold transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-text-line"></i>
                    Read Executive Summary
                  </a>
                </div>
              </div>

              {/* Book Overview */}
              <div className="max-w-2xl">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Book Overview</h2>
                <p className="text-lg text-slate-700 leading-relaxed mb-4">
                  <strong>"The Constitutional AI Implementation Playbook: Engineering Auditable Trust for High-Stakes Systems"</strong> is a comprehensive academic work that transforms AI safety from philosophical debate into engineering practice.
                </p>
                <p className="text-lg text-slate-700 leading-relaxed">
                  This book provides the first comprehensive legal and technical framework for auditable, enforceable AI safety. Drawing on landmark cases like <em>Garcia v. Character.AI</em> and emerging regulatory frameworks including the NIST AI RMF, it establishes Constitutional AI as the engineering solution to the black box problem in AI liability.
                </p>
              </div>
            </div>
          </section>

          {/* Book Proposal Section */}
          <section className="mb-16 bg-slate-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Book Proposal for Academic Publishers</h2>
            
            {/* Overview */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Overview</h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                <strong>The Constitutional AI Implementation Playbook</strong> bridges the critical gap between technical AI alignment research and product liability law. As generative AI systems transition from experimental models to critical infrastructure, courts are beginning to hold developers accountable for foreseeable harms. This book provides the first comprehensive legal and technical framework for auditable, enforceable AI safety.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Drawing on landmark cases like <em>Garcia v. Character.AI</em> and emerging regulatory frameworks including the NIST AI RMF, this work establishes Constitutional AI as the engineering solution to the "black box" problem in AI liability. It is essential reading for policymakers, AI developers, legal practitioners, and researchers seeking to understand how explicit normative constraints can transform AI systems from opaque statistical models into auditable, accountable products.
              </p>
            </div>

            {/* Market Analysis */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Market Analysis</h3>
              
              <h4 className="text-xl font-semibold text-slate-900 mb-3">Target Audiences</h4>
              <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
                <li><strong>Primary:</strong> Policymakers, AI governance professionals, legal practitioners specializing in technology law</li>
                <li><strong>Secondary:</strong> AI safety researchers, ML engineers, corporate compliance officers</li>
                <li><strong>Academic:</strong> Graduate courses in AI policy, technology law, computer science ethics</li>
                <li><strong>Professional:</strong> Insurance underwriters, risk management consultants, expert witnesses</li>
              </ul>

              <h4 className="text-xl font-semibold text-slate-900 mb-3">Competitive Analysis</h4>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-slate-300">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Book</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Focus</th>
                      <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Gap This Book Fills</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700"><em>Human Compatible</em> (Russell)</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Philosophical AI alignment</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">No legal framework or implementation guide</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-3 text-slate-700"><em>The Alignment Problem</em> (Christian)</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Technical ML challenges</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">No product liability analysis</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700"><em>AI Law</em> (Leenes et al.)</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">General legal issues</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">No technical alignment solution</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-3 text-slate-700"><em>Robot Rules</em> (Calo)</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Robotics policy</td>
                      <td className="border border-slate-300 px-4 py-3 text-slate-700">Pre-LLM era, no GenAI focus</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-lg text-slate-700 leading-relaxed">
                <strong>Unique Positioning:</strong> This is the first book to integrate Constitutional AI technical methodology with product liability doctrine, providing both theoretical foundation and practical implementation guidance.
              </p>
            </div>

            {/* Author Credentials */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Author Credentials</h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                <strong>Alberto Rocha</strong> is Director of the Algorithmic Consistency Initiative, LLC, and a Congressional appointee with 40 years of experience in technology and policy. He is the author of 19 books on AI and host of the 200-episode podcast "AI and Us: Exploring Our Future."
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg text-slate-700 ml-4">
                <li>Originator of "The Mirror Effect" theory of AI behavioral mimicry</li>
                <li>Expert witness on AI product liability cases</li>
                <li>Advisor to state AI governance task forces</li>
                <li>Published researcher on Constitutional AI frameworks</li>
              </ul>
            </div>

            {/* Manuscript Details */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Manuscript Details</h3>
              <ul className="list-disc list-inside space-y-2 text-lg text-slate-700 ml-4">
                <li><strong>Estimated Length:</strong> 120,000-140,000 words (400-450 pages)</li>
                <li><strong>Completion Date:</strong> Q4 2025</li>
                <li><strong>Format:</strong> Hardcover, paperback, and open-access digital edition</li>
                <li><strong>Supplementary Materials:</strong> Policy Card templates, NIST RMF mapping guides, case law database</li>
                <li><strong>Illustrations:</strong> 25-30 figures, 15-20 tables, technical diagrams</li>
              </ul>
            </div>

            {/* Marketing Plan */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Marketing & Promotion Plan</h3>
              <ul className="list-disc list-inside space-y-2 text-lg text-slate-700 ml-4">
                <li><strong>Academic Conferences:</strong> ACM FAccT, NeurIPS, AAAI, Law & Society Association</li>
                <li><strong>Policy Briefings:</strong> Congressional AI Caucus, NIST AI Safety Institute, state task forces</li>
                <li><strong>Media Outreach:</strong> Op-eds in The Hill, Lawfare, MIT Technology Review</li>
                <li><strong>Professional Training:</strong> CLE courses for lawyers, workshops for AI developers</li>
                <li><strong>Course Adoption:</strong> Target 50+ universities for AI policy and technology law courses</li>
              </ul>
            </div>
          </section>

          {/* Detailed Chapter Outline */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Detailed Chapter Outline</h2>

            {/* Part I */}
            <div className="mb-12 bg-red-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">PART I: THE CRISIS OF ACCOUNTABILITY</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 1: The Black Box Problem</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 8,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>The opacity crisis in generative AI systems</li>
                  <li>Why traditional product liability fails for AI</li>
                  <li>The "foreseeability" defense and its erosion</li>
                  <li>Case study: <em>Garcia v. Character.AI</em> - The watershed moment</li>
                  <li>The need for structural accountability</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 1.1-1.4
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 2: The Mirror Effect</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 10,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>How AI systems mirror human inconsistency</li>
                  <li>The biological foundations of moral preference</li>
                  <li>The "Digital Mirror" vs. the "Biological Mirror"</li>
                  <li>Why behavioral mimicry is a design defect</li>
                  <li>The Consistency Paradox: Internal vs. external alignment</li>
                  <li>Empirical evidence from RLHF failures</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Mirror Effect white paper Sections I-III
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 3: The Failure of RLHF as a Safety Standard</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 9,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Technical analysis of RLHF methodology</li>
                  <li>Four critical flaws: Inconsistency, sycophancy, opacity, jailbreakability</li>
                  <li>The "Data Mirror Effect" in training datasets</li>
                  <li>Case studies: Obermeyer healthcare bias, Replit AI disaster</li>
                  <li>Why RLHF cannot establish "reasonable care"</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 1.2
                </p>
              </div>
            </div>

            {/* Part II */}
            <div className="mb-12 bg-slate-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">PART II: CONSTITUTIONAL AI - TECHNICAL FOUNDATIONS</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 4: What is Constitutional AI?</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 10,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Origins: Anthropic's breakthrough methodology</li>
                  <li>The two-stage process: Supervised learning + RL from AI Feedback</li>
                  <li>How constitutions encode normative constraints</li>
                  <li>Technical comparison: CAI vs. RLHF vs. Safe RLHF</li>
                  <li>The role of Constitutional Classifiers</li>
                  <li>Code examples and implementation patterns</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 2.1-2.3
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 5: Policy Cards - Machine-Readable Constitutions</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 8,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>The need for standardized constitutional formats</li>
                  <li>Policy Card schema: Controls, obligations, assurance mapping</li>
                  <li>Attribute-Based Access Control (ABAC) for AI</li>
                  <li>Linking technical controls to governance frameworks</li>
                  <li>Template library and implementation guide</li>
                  <li>Version control and constitutional evolution</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 2.4
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 6: Constitutional Classifiers and Runtime Enforcement</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 9,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Training classifiers for constitutional compliance</li>
                  <li>Defense-in-depth: Training-time vs. runtime enforcement</li>
                  <li>Handling false positives and adversarial attacks</li>
                  <li>The "Declare-Do-Audit" lifecycle</li>
                  <li>Monitoring and post-deployment surveillance</li>
                  <li>Case study: Implementing guardrails in production systems</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>New content:</strong> Technical deep-dive with engineering examples
                </p>
              </div>
            </div>

            {/* Part III */}
            <div className="mb-12 bg-red-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">PART III: LEGAL FRAMEWORKS AND LIABILITY</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 7: Product Liability Doctrine Applied to AI</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 11,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Restatement (Third) of Torts: Design defect standard</li>
                  <li>The Risk-Utility Test applied to behavioral mimicry</li>
                  <li>Reasonable Alternative Design (RAD): Safe RLHF and CAI</li>
                  <li>Manufacturing defect: Training data as "raw materials"</li>
                  <li>Failure to warn: Anthropomorphism without disclosure</li>
                  <li>Strict liability vs. negligence in AI cases</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> AI Liability white paper Section V
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 8: Case Law Analysis - The New Frontier</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 12,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><em>Garcia v. Character.AI</em>: Design defect and anthropomorphism</li>
                  <li><em>Raine v. OpenAI</em>: Professional negligence and malice</li>
                  <li>Obermeyer healthcare bias: Disparate impact and discrimination</li>
                  <li>Replit AI disaster: Corporate liability and access control</li>
                  <li>Emerging patterns in AI tort litigation</li>
                  <li>Judicial reasoning and the "black box" defense</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> AI Liability white paper Section IV
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 9: Constitutional AI as Legal Defense</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 10,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Establishing "reasonable care" through constitutional documentation</li>
                  <li>Auditability: Inspecting the constitution vs. the black box</li>
                  <li>Verifying adherence through Constitutional Classifiers</li>
                  <li>Chain of Thought reasoning as audit trail</li>
                  <li>Overcoming the "foreseeability" barrier</li>
                  <li>Litigation strategy for developers and plaintiffs</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 3
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 10: Law-Following AI and Agency Theory</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 8,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>The concept of "Law-Following AI" (LFAI)</li>
                  <li>Agency law applied to AI systems</li>
                  <li>The duty to refuse illegal instructions</li>
                  <li>Preventing "AI Henchmen" through constitutional constraints</li>
                  <li>Safe harbor effects of constitutional compliance</li>
                  <li>Criminal liability and corporate responsibility</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 3.3
                </p>
              </div>
            </div>

            {/* Part IV */}
            <div className="mb-12 bg-slate-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">PART IV: POLICY IMPLEMENTATION AND GOVERNANCE</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 11: Integrating CAI into the NIST AI RMF</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 11,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Overview of NIST AI Risk Management Framework</li>
                  <li>GOVERN: Establishing the machine constitution</li>
                  <li>MAP: Contextualizing risks via Policy Cards</li>
                  <li>MEASURE: Verifying constitutional adherence</li>
                  <li>MANAGE: Runtime enforcement and the Declare-Do-Audit cycle</li>
                  <li>Recommendations for framework evolution</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> CAI Engineering Standard Section 4
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 12: Regulatory Pathways and Standards</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 10,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>NIST AI RMF as minimum standard of care</li>
                  <li>FTC Impersonation Rule: Classifying mimicry as deception</li>
                  <li>The AI LEAD Act and federal product classification</li>
                  <li>State-level AI regulation and the consistency crisis</li>
                  <li>International frameworks: EU AI Act, UK approach</li>
                  <li>Industry self-regulation vs. mandatory standards</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> AI Liability white paper Section VI + State of AI Report
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 13: The Third Way - Empirical Governance</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 9,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Beyond precautionary bans and laissez-faire approaches</li>
                  <li>Evidence-based safety specifications</li>
                  <li>The role of Constitutional Error Rate (CER) metrics</li>
                  <li>Post-market surveillance and "Digital Recalls"</li>
                  <li>Insurance markets and liability pricing</li>
                  <li>Creating the "uninsurability threshold" for unsafe AI</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> CAI Engineering Standard Introduction + Section 4
                </p>
              </div>
            </div>

            {/* Part V */}
            <div className="mb-12 bg-red-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">PART V: IMPLEMENTATION AND FUTURE DIRECTIONS</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 14: Constitutional Drafting - A Practical Guide</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 10,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>The interdisciplinary drafting process</li>
                  <li>Balancing competing values and resolving conflicts</li>
                  <li>Avoiding interpretive ambiguity in natural language rules</li>
                  <li>Domain-specific constitutions: Healthcare, finance, education</li>
                  <li>Iterative refinement and constitutional evolution</li>
                  <li>Template library and best practices</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>New content:</strong> Practical implementation guide
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 15: Enterprise Adoption and Case Studies</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 11,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Case study: Healthcare AI with constitutional constraints</li>
                  <li>Case study: Financial services compliance</li>
                  <li>Case study: Content moderation at scale</li>
                  <li>ROI analysis: Cost of implementation vs. liability exposure</li>
                  <li>Change management and organizational adoption</li>
                  <li>Lessons learned from early adopters</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>New content:</strong> Real-world implementation examples
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 16: Limitations and Open Challenges</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 8,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>The constitution drafting problem and interpretive ambiguity</li>
                  <li>Classifier robustness: False positives and adversarial attacks</li>
                  <li>Base model dependency and bias inheritance</li>
                  <li>Computational costs and scalability challenges</li>
                  <li>Cross-cultural constitutional differences</li>
                  <li>Research agenda for next-generation CAI</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>Expands:</strong> Constitutional AI white paper Section 5
                </p>
              </div>

              <div className="mb-8">
                <h4 className="text-xl font-bold text-slate-900 mb-3">Chapter 17: The Future of AI Accountability</h4>
                <p className="text-slate-700 mb-3"><em>Estimated: 9,000 words</em></p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>From behavioral mimicry to principled alignment</li>
                  <li>The evolution of AI governance frameworks</li>
                  <li>Constitutional AI for AGI safety</li>
                  <li>International cooperation and standard harmonization</li>
                  <li>The role of civil society and public participation</li>
                  <li>Vision: Trustworthy AI as critical infrastructure</li>
                </ul>
                <p className="text-slate-700 mt-3 italic">
                  <strong>New content:</strong> Forward-looking synthesis
                </p>
              </div>
            </div>

            {/* Appendices */}
            <div className="mb-12 bg-slate-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">APPENDICES</h3>
              
              <ul className="list-disc list-inside space-y-3 text-lg text-slate-700 ml-4">
                <li><strong>Appendix A:</strong> Policy Card JSON Schema Specification</li>
                <li><strong>Appendix B:</strong> Sample Constitutional Templates by Domain</li>
                <li><strong>Appendix C:</strong> NIST AI RMF Mapping Guide</li>
                <li><strong>Appendix D:</strong> Constitutional Classifier Training Datasets</li>
                <li><strong>Appendix E:</strong> Case Law Database and Legal Citations</li>
                <li><strong>Appendix F:</strong> Glossary of Technical and Legal Terms</li>
                <li><strong>Appendix G:</strong> Implementation Checklist for Developers</li>
                <li><strong>Appendix H:</strong> Model Legislation and Policy Language</li>
              </ul>
            </div>
          </section>

          {/* Chapter Expansion Matrix */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Chapter Expansion Matrix</h2>
            <p className="text-lg text-slate-700 mb-6">
              This table shows how existing white papers map to book chapters and what new content will be developed:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Book Chapter</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Source White Paper</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Expansion Type</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">New Content %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 1: Black Box Problem</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 1)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Deep expansion + case studies</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">40%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 2: Mirror Effect</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Mirror Effect (Full)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Expansion + empirical evidence</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">30%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 3: RLHF Failures</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 1.2)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Technical deep-dive</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">50%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 4: What is CAI?</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 2)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Technical expansion + code</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">40%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 5: Policy Cards</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 2.4)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Implementation guide</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">60%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 6: Classifiers</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 2.3)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Engineering deep-dive</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">70%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 7: Product Liability</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">AI Liability (Sec V)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Legal doctrine expansion</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">35%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 8: Case Law</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">AI Liability (Sec IV)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Comprehensive analysis</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">30%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 9: CAI as Defense</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 3)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Litigation strategy</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">45%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 10: Law-Following AI</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 3.3)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Agency theory expansion</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">50%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 11: NIST Integration</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">CAI Engineering (Sec 4)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Implementation guide</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">40%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 12: Regulatory Paths</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">AI Liability (Sec VI) + State of AI</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Policy synthesis</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">35%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 13: Third Way</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">CAI Engineering (Intro)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Governance framework</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">55%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 14: Drafting Guide</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">New Content</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Practical handbook</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">90%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 15: Case Studies</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">New Content</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Enterprise examples</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">95%</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 16: Limitations</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Constitutional AI (Sec 5)</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Research agenda</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">40%</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Ch 17: Future</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Synthesis of All Papers</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Forward-looking vision</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">80%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Writing Timeline */}
          <section className="mb-16 bg-slate-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Proposed Writing Timeline</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 1: Foundation (Months 1-3)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete Part I (Chapters 1-3): The Crisis of Accountability</li>
                  <li>Expand Mirror Effect white paper into comprehensive Chapter 2</li>
                  <li>Conduct additional case law research for Chapter 1</li>
                  <li>Develop technical diagrams and figures</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 2: Technical Core (Months 4-6)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete Part II (Chapters 4-6): Constitutional AI Technical Foundations</li>
                  <li>Develop Policy Card schema and templates</li>
                  <li>Create code examples and implementation patterns</li>
                  <li>Interview AI engineers for practical insights</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 3: Legal Framework (Months 7-9)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete Part III (Chapters 7-10): Legal Frameworks and Liability</li>
                  <li>Comprehensive case law analysis and updates</li>
                  <li>Develop litigation strategy frameworks</li>
                  <li>Consult with legal practitioners and expert witnesses</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 4: Policy Implementation (Months 10-12)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete Part IV (Chapters 11-13): Policy Implementation and Governance</li>
                  <li>Develop NIST RMF integration guides</li>
                  <li>Analyze international regulatory frameworks</li>
                  <li>Create model legislation and policy language</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 5: Practical Application (Months 13-15)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete Part V (Chapters 14-17): Implementation and Future Directions</li>
                  <li>Develop enterprise case studies</li>
                  <li>Create constitutional drafting templates</li>
                  <li>Write forward-looking synthesis chapter</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phase 6: Finalization (Months 16-18)</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Complete all appendices and supplementary materials</li>
                  <li>Comprehensive editing and fact-checking</li>
                  <li>Peer review by technical and legal experts</li>
                  <li>Final manuscript preparation and submission</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mt-16 p-8 bg-slate-100 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Publisher Inquiries</h3>
            <p className="text-lg text-slate-700 mb-6">
              Interested in publishing this groundbreaking work? Contact us to discuss manuscript samples, detailed chapter outlines, and publication timeline.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Contact for Full Proposal
            </a>
          </section>
        </article>

        <Footer />
      </div>
    </>
  );
}
